import {
  BiqliAnalytics,
  inject,
  teardownInjectedInstance,
  type BiqliAnalyticsConfig,
  type StripeSessionData,
  type TrackClickInput,
  type TrackLeadInput,
  type TrackSaleInput,
} from './index';

export interface BiqliGlobal {
  init(config: BiqliAnalyticsConfig): BiqliAnalytics;
  getInstance(): BiqliAnalytics | null;
  trackClick(input: TrackClickInput): ReturnType<BiqliAnalytics['trackClick']>;
  trackLead(input: TrackLeadInput): ReturnType<BiqliAnalytics['trackLead']>;
  trackSale(input: TrackSaleInput): ReturnType<BiqliAnalytics['trackSale']>;
  storeStripeSession(data: StripeSessionData): ReturnType<BiqliAnalytics['storeStripeSession']>;
  getClickId(): string | null;
  setClickId(clickId: string): void;
  clearClickId(): void;
  setConsent(granted: boolean): void;
  decorateUrl(url: string | URL): string;
  teardown(): void;
}

declare global {
  interface Window {
    Biqli?: BiqliGlobal;
    BiqliSDKInstance?: BiqliAnalytics;
  }
}

let instance: BiqliAnalytics | null = null;

function requireInstance(): BiqliAnalytics {
  if (!instance) throw new Error('Biqli has not been initialized. Call Biqli.init first.');
  return instance;
}

const globalApi: BiqliGlobal = {
  init(config) {
    instance = inject(config);
    window.BiqliSDKInstance = instance;
    window.dispatchEvent(new CustomEvent('biqli:ready', {detail: {sdk: instance}}));
    return instance;
  },
  getInstance: () => instance,
  trackClick: input => requireInstance().trackClick(input),
  trackLead: input => requireInstance().trackLead(input),
  trackSale: input => requireInstance().trackSale(input),
  storeStripeSession: data => requireInstance().storeStripeSession(data),
  getClickId: () => instance?.getClickId() ?? null,
  setClickId: clickId => requireInstance().setClickId(clickId),
  clearClickId: () => requireInstance().clearClickId(),
  setConsent: granted => requireInstance().setConsent(granted),
  decorateUrl: url => requireInstance().decorateUrl(url),
  teardown() {
    teardownInjectedInstance();
    instance = null;
    delete window.BiqliSDKInstance;
  },
};

function parseBoolean(value: string | undefined): boolean | undefined {
  if (value === undefined) return undefined;
  return !['false', '0', 'no', 'off'].includes(value.toLowerCase());
}

function parseList(value: string | undefined): string[] | undefined {
  if (!value) return undefined;
  return value.split(',').map(item => item.trim()).filter(Boolean);
}

function configFromScript(script: HTMLScriptElement): BiqliAnalyticsConfig | null {
  const publishableKey = script.dataset.publishableKey?.trim();
  if (!publishableKey) return null;
  return {
    publishableKey,
    apiHost: script.dataset.apiHost,
    attributionModel: script.dataset.attributionModel === 'first-click'
      ? 'first-click'
      : 'last-click',
    outboundDomains: parseList(script.dataset.outboundDomains),
    queryParameters: parseList(script.dataset.queryParameters),
    manualStart: parseBoolean(script.dataset.manualStart),
    consent: parseBoolean(script.dataset.consent),
  };
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  window.Biqli = globalApi;
  const script = document.currentScript instanceof HTMLScriptElement
    ? document.currentScript
    : document.querySelector<HTMLScriptElement>('script[data-publishable-key][src*="biqli"]');
  const config = script ? configFromScript(script) : null;
  if (config) globalApi.init(config);
}

export default globalApi;
